import { useQuery } from '@tanstack/react-query';

import { DrawerLink } from '../../app/DrawerLink';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { PlaylistCover } from './PlaylistCover';
import { playlists } from './api';

/**
 * Somebody's playlists, on their profile.
 *
 * The same grid as the albums, because on a profile they are the same kind
 * of thing: a set of tracks somebody put together and gave a name. A
 * visitor sees the public ones; the owner also sees their private and
 * unlisted ones, badged, so a list they made does not look like it failed
 * to save. Making one still happens from the player bar and the library,
 * where the tracks are; this is where they are shown.
 */
export function PlaylistGrid({ username, isOwner }: { username: string; isOwner: boolean }) {
  const { data, isPending } = useQuery({
    queryKey: ['profile-playlists', username],
    queryFn: () => playlists.mine(username),
  });

  if (isPending) {
    return (
      <div className="mt-4 flex flex-col gap-2" aria-busy="true">
        {[0, 1].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    // A visitor gets no heading over nothing; the owner gets told how.
    if (!isOwner) return null;

    return (
      <Card className="mt-4 p-6">
        <p className="text-sm text-soft">
          No playlists yet. The plus beside any track starts one, and a public playlist shows
          here for anybody to follow.
        </p>
      </Card>
    );
  }

  return (
    <ul className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-playlist-grid>
      {items.map((list) => (
        <li key={list.id} data-playlist-card={list.id}>
          <DrawerLink to={`/playlists/${list.id}`} className="group block">
            <Card className="overflow-hidden transition-colors group-hover:border-line-lit">
              <div className="aspect-square w-full overflow-hidden bg-raised">
                <PlaylistCover artwork={list.artwork_small} mosaic={list.mosaic} rounded="" className="h-full w-full" />
              </div>

              <div className="p-3">
                <p className="truncate text-sm font-medium text-ink">{list.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="meta-caps">
                    {list.track_count}{' '}
                    {list.track_count === 1 ? copy['noun.track'] : copy['noun.track.plural']}
                  </span>
                  {list.visibility !== 'public' && (
                    <Chip>{list.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
                  )}
                </div>
              </div>
            </Card>
          </DrawerLink>
        </li>
      ))}
    </ul>
  );
}
